import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import {
  readyToUploadCases,
  caseCount,
  CaseNotes,
  unallocatedCases,
  last12HoursCases,
  last24HoursCases,
  missingFilledOrderCase,
} from "../../types/ReadyCasesTypes";
import api from "../../utils/api";

export type AllocationCasesSyncPayload = {
  upsert: unallocatedCases[];
  affectedCaseIds: string[];
  affectedAllocationIds: number[];
};

const getUnallocatedRowKey = (row: unallocatedCases) => {
  if (
    row.type === "allocated" ||
    row.type === "hold" ||
    row.type === "reallocation"
  ) {
    return `alloc:${row.allocation_id}`;
  }

  return `${row.case_id}:${row.type}`;
};

interface ReadyCasesState {
  unallocatedCases: unallocatedCases[];
  readyToUpload: readyToUploadCases[];
  caseNotes: CaseNotes[];
  last12HoursCases: last12HoursCases[];
  last24HoursCases: last24HoursCases[];
  missingFilledOrderCases: missingFilledOrderCase[];
  caseCount:caseCount;
  loading: boolean;
  error: string | null;
}

const initialState: ReadyCasesState = {
  unallocatedCases : [],
  readyToUpload: [],
  caseNotes: [],
  last12HoursCases: [],
  last24HoursCases: [],
  missingFilledOrderCases: [],
  caseCount : {live: 0,
    redesign: 0,
    reallocation: 0,},
  loading: false,
  error: null,
};


export const getAllUnallocatedCases = createAsyncThunk<
  unallocatedCases[],
  void,
  { rejectValue: string }
>("allocation/getAllUnallocatedCases", async (_, { rejectWithValue }) => {
  try {
    const res = await api.get(
      `/api/allocation/unallocated/all-cases`
    );
    return res.data.data;
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch unallocated cases"
      );
    }
    return rejectWithValue("Failed to fetch unallocated cases");
  }
});

// Fetch Ready to upload cases slice
export const fetchReadyToUploadCases = createAsyncThunk<
  readyToUploadCases[],
  void,
  { rejectValue: string }
>("readyCases/fetchReadyToUpload", async (_, { rejectWithValue }) => {
  try {
    const res = await api.get(
      `/api/allocation/upload/ready-cases`
    );
    return res.data.data;
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch ready to upload cases"
      );
    }
    return rejectWithValue("Failed to fetch ready to upload cases");
  }
});

// Fetch case notes by allocation id cases slice
export const fetchCaseNotesByAllocationId = createAsyncThunk<
  CaseNotes[],
  { allocationId?: number | null; caseId?: string | null },
  { rejectValue: string }
>(
  "readyCases/fetchCaseNotesByAllocationId",
  async ({ allocationId, caseId }, { rejectWithValue }) => {
    try {
      if (!allocationId && !String(caseId || "").trim()) {
        return rejectWithValue("Failed to fetch case notes by allocation id");
      }
      const res = allocationId
        ? await api.get(`/api/allocation/case/notes/${allocationId}`)
        : await api.get(
            `/api/allocation/case/notes/by-case/${encodeURIComponent(
              String(caseId || "").trim(),
            )}`,
          );
      return res.data.data;
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message ||
            "Failed to fetch case notes by allocation id"
        );
      }
      return rejectWithValue("Failed to fetch case notes by allocation id");
    }
  }
);


// Fetch case notes by allocation id cases slice
export const fetchpendingCasesCounts = createAsyncThunk<
caseCount,
  void,
  { rejectValue: string }
>(
  "readyCases/fetchpendingCasesCounts",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/api/allocation/pending/cases-count`
      );
      return res.data.data as caseCount;
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message ||
            "Failed to fetch pending case counts"
        );
      }
      return rejectWithValue("Failed to fetch pending case counts");
    }
  }
);


export const fetchLast12HoursCases = createAsyncThunk<
  last12HoursCases[],
  void,
  { rejectValue: string }
>(
  "readyCases/fetchLast12HoursCases",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/api/allocation/allocated/last-12-hours-cases`
      );
      return res.data.data as last12HoursCases[];
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to fetch last 12 hours cases"
        );
      }
      return rejectWithValue("Failed to fetch last 12 hours cases");
    }
  }
);

export const fetchLast24HoursPendingCases = createAsyncThunk<
  last24HoursCases[],
  void,
  { rejectValue: string }
>(
  "readyCases/fetchLast24HoursPendingCases",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/api/allocation/allocated/last-24-hours-pending-cases`
      );
      return res.data.data as last24HoursCases[];
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to fetch last 24 hours pending cases"
        );
      }
      return rejectWithValue("Failed to fetch last 24 hours pending cases");
    }
  }
);

export const fetchMissingFilledOrderCases = createAsyncThunk<
  missingFilledOrderCase[],
  void,
  { rejectValue: string }
>(
  "readyCases/fetchMissingFilledOrderCases",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/api/allocation/filled-order/missing-zip-cases`,
      );
      return res.data.data as missingFilledOrderCase[];
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message ||
            "Failed to fetch filled order cases missing zip",
        );
      }
      return rejectWithValue("Failed to fetch filled order cases missing zip");
    }
  },
);

export const makeMissingFilledOrderZip = createAsyncThunk<
  { caseId: string; zipFileName: string },
  { caseId: string; userId?: number },
  { rejectValue: string }
>(
  "readyCases/makeMissingFilledOrderZip",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await api.post(`/api/allocation/filled-order/make-zip`, payload);
      return {
        caseId: payload.caseId,
        zipFileName: res.data?.data?.zipFileName as string,
      };
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to make zip",
        );
      }
      return rejectWithValue("Failed to make zip");
    }
  },
);



const readyCasesSlice = createSlice({
  name: "readyCases",
  initialState,
  reducers: {
    removeReadyToUploadCases: (
      state,
      action: PayloadAction<number | number[]>,
    ) => {
      const ids = new Set(
        (Array.isArray(action.payload) ? action.payload : [action.payload]).map(
          Number,
        ),
      );
      state.readyToUpload = state.readyToUpload.filter(
        (c) => !ids.has(Number(c.allocation_id)),
      );
    },
    upsertReadyToUploadCase: (
      state,
      action: PayloadAction<readyToUploadCases>,
    ) => {
      const row = action.payload;
      const isFz = String(row.case_id || "")
        .toUpperCase()
        .startsWith("FZ");
      // FZ cases appear once on Ready to Upload (per case_id), not per patient.
      const existingIndex = state.readyToUpload.findIndex((c) =>
        isFz
          ? c.case_id === row.case_id
          : c.allocation_id === row.allocation_id,
      );
      if (existingIndex >= 0) {
        state.readyToUpload[existingIndex] = row;
      } else {
        state.readyToUpload.unshift(row);
      }
    },
    applyAllocationCasesSync: (
      state,
      action: PayloadAction<AllocationCasesSyncPayload>,
    ) => {
      const { upsert, affectedCaseIds, affectedAllocationIds } = action.payload;
      const affectedCaseIdSet = new Set(affectedCaseIds);
      const affectedAllocationIdSet = new Set(
        affectedAllocationIds.filter((id) => id != null),
      );
      const upsertByKey = new Map(
        upsert.map((row) => [getUnallocatedRowKey(row), row]),
      );

      state.unallocatedCases = state.unallocatedCases.filter((row) => {
        const caseAffected = affectedCaseIdSet.has(row.case_id);
        const allocAffected =
          row.allocation_id != null &&
          affectedAllocationIdSet.has(row.allocation_id);

        if (!caseAffected && !allocAffected) return true;

        return upsertByKey.has(getUnallocatedRowKey(row));
      });

      for (const row of upsert) {
        const key = getUnallocatedRowKey(row);
        const existingIndex = state.unallocatedCases.findIndex(
          (item) => getUnallocatedRowKey(item) === key,
        );

        if (existingIndex >= 0) {
          state.unallocatedCases[existingIndex] = row;
        } else {
          state.unallocatedCases.push(row);
        }
      }
    },
  },

  extraReducers: (builder) => {
    builder
          .addCase(getAllUnallocatedCases.pending, (state) => {
            state.loading = true;
            state.error = null;
          })
          .addCase(
            getAllUnallocatedCases.fulfilled,
            (state, action: PayloadAction<unallocatedCases[]>) => {
              state.loading = false;
              state.unallocatedCases = action.payload;
            }
          )
          .addCase(getAllUnallocatedCases.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || "Error fetching allocated cases";
          })
      // READY TO UPLOAD CASES
      .addCase(fetchReadyToUploadCases.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchReadyToUploadCases.fulfilled,
        (state, action: PayloadAction<readyToUploadCases[]>) => {
          state.loading = false;
          state.readyToUpload = action.payload;
        }
      )
      .addCase(fetchReadyToUploadCases.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error fetching ready to upload cases";
      })

      // CASE NOTES BY ALLOCATION ID
      .addCase(fetchCaseNotesByAllocationId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCaseNotesByAllocationId.fulfilled, (state, action) => {
        state.loading = false;
        state.caseNotes = action.payload;
      })
      .addCase(fetchCaseNotesByAllocationId.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || "Error fetching case notes by allocation id";
      })

      // LAST 12 HOURS CASES
      .addCase(fetchLast12HoursCases.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLast12HoursCases.fulfilled, (state, action) => {
        state.loading = false;
        state.last12HoursCases = action.payload;
      })
      .addCase(fetchLast12HoursCases.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || "Error fetching last 12 hours cases";
      })

      // LAST 24 HOURS CASES
      .addCase(fetchLast24HoursPendingCases.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLast24HoursPendingCases.fulfilled, (state, action) => {
        state.loading = false;
        state.last24HoursCases = action.payload;
      })
      .addCase(fetchLast24HoursPendingCases.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || "Error fetching last 24 hours cases";
      })

      // FILLED ORDER CASES MISSING ZIP / ALLOCATION
      .addCase(fetchMissingFilledOrderCases.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMissingFilledOrderCases.fulfilled, (state, action) => {
        state.loading = false;
        state.missingFilledOrderCases = action.payload;
      })
      .addCase(fetchMissingFilledOrderCases.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || "Error fetching filled order cases missing zip";
      })
      .addCase(makeMissingFilledOrderZip.fulfilled, (state, action) => {
        state.missingFilledOrderCases = state.missingFilledOrderCases.filter(
          (c) => c.case_id !== action.payload.caseId,
        );
      })

      // CASE COUNTS
      .addCase(fetchpendingCasesCounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchpendingCasesCounts.fulfilled, (state, action) => {
        state.loading = false;
        state.caseCount = action.payload;
      })
      .addCase(fetchpendingCasesCounts.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || "Failed to fetch pending case counts";
      })
      
      
      ;
  },
});

export default readyCasesSlice.reducer;
export const { removeReadyToUploadCases, upsertReadyToUploadCase, applyAllocationCasesSync } =
  readyCasesSlice.actions;
